import type { ReactElement } from "react";

import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { STUDIO_FORMAT_CHOICES, STUDIO_STAFF_CHOICES } from "./studioFilms";
import type { StudioDirectory } from "./useStudioDirectory";

/**
 * Studio film filters for the Echo and session lists (B6.3). The dashboard API
 * filters on them — deploy it first: an older API answers unknown filter keys
 * with no rows. Agency choices come from the admin-only directory, so
 * non-admins get no agency picker (a deep link still filters). The creator
 * filter is a visible text field so the agencies' "Films" links show it.
 */
export const studioFilmFilters = (
  agencyChoices: readonly { id: string; name: string }[],
): ReactElement[] => [
  ...(agencyChoices.length
    ? [
        <SelectInput
          source="studio_agency_id"
          label="Agency"
          emptyText="Any agency"
          choices={[...agencyChoices]}
          optionText="name"
          optionValue="id"
        />,
      ]
    : []),
  <TextInput source="studio_created_by" label="Creator (portal user id)" />,
  <SelectInput
    source="studio_created_by_staff"
    label="Made by staff"
    emptyText="Staff or client"
    choices={[...STUDIO_STAFF_CHOICES]}
    optionText="name"
    optionValue="id"
  />,
  <SelectInput
    source="studio_format"
    label="Format"
    emptyText="Any format"
    choices={[...STUDIO_FORMAT_CHOICES]}
    optionText="name"
    optionValue="id"
  />,
];

/** Agency choices for the filter, from a loaded directory. */
export const studioAgencyChoices = (directory: StudioDirectory | null) =>
  (directory?.agencies ?? []).map((agency) => ({
    id: String(agency.id),
    name: agency.name,
  }));
